import { Edit3, Save, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { GeoPoint } from '../types';
import { validateCoordinates } from '../utils';

interface EditablePointRowProps {
  point: GeoPoint;
  index: number;
  onUpdate: (index: number, lat: number, lng: number) => void;
  onDelete: (index: number) => void;
  disabled: boolean;
  prefix?: string;
}

export const EditablePointRow = ({
  point,
  index,
  onUpdate,
  onDelete,
  disabled,
  prefix = '',
}: EditablePointRowProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [latValue, setLatValue] = useState(point.lat.toFixed(6));
  const [lngValue, setLngValue] = useState(point.lng.toFixed(6));
  const [error, setError] = useState<string | null>(null);

  const handleSave = () => {
    const validation = validateCoordinates(latValue, lngValue);
    if (!validation.valid) {
      setError(validation.error ?? 'Invalid coordinates');
      return;
    }

    onUpdate(index, parseFloat(latValue), parseFloat(lngValue));
    setIsEditing(false);
    setError(null);
  };

  const handleCancel = () => {
    setLatValue(point.lat.toFixed(6));
    setLngValue(point.lng.toFixed(6));
    setIsEditing(false);
    setError(null);
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2 text-xs text-[#621C43] bg-[#F8EFF3] px-3 py-1.5 rounded border border-pink-200">
        <span className="font-bold w-6 text-[#C0275E]">
          {prefix}
          {index + 1}
        </span>

        {isEditing ? (
          <>
            <div className="flex-1 flex gap-2">
              <input
                type="number"
                step="any"
                value={latValue}
                onChange={(e) => setLatValue(e.target.value)}
                placeholder="Lat"
                className="w-24 px-1 py-0.5 text-xs border border-[#C0275E] rounded"
              />
              <input
                type="number"
                step="any"
                value={lngValue}
                onChange={(e) => setLngValue(e.target.value)}
                placeholder="Lng"
                className="w-24 px-1 py-0.5 text-xs border border-[#C0275E] rounded"
              />
            </div>
            <button
              type="button"
              onClick={handleSave}
              className="p-1 text-green-600 hover:bg-green-50 rounded"
              title="Save"
            >
              <Save size={12} />
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="p-1 text-gray-500 hover:bg-gray-50 rounded"
              title="Cancel"
            >
              <X size={12} />
            </button>
          </>
        ) : (
          <>
            <span className="flex-1">
              Lat: {point.lat.toFixed(5)}, Lng: {point.lng.toFixed(5)}
            </span>
            {!disabled && (
              <>
                <button
                  type="button"
                  onClick={() => setIsEditing(true)}
                  className="p-1 text-[#C0275E] hover:bg-pink-100 rounded"
                  title="Edit"
                >
                  <Edit3 size={12} />
                </button>
                <button
                  type="button"
                  onClick={() => onDelete(index)}
                  className="p-1 text-red-500 hover:bg-red-50 rounded"
                  title="Delete"
                >
                  <Trash2 size={12} />
                </button>
              </>
            )}
          </>
        )}
      </div>
      {error && <p className="text-xs text-red-500 px-3">{error}</p>}
    </div>
  );
};
